<template>
    <NonSkuStoneModal
        :data-qa="dataQa"
        :is-open="!!stone"
        :is-added="
            ringBuilderStone?.__typename === 'NonSkuGemstone' &&
            ringBuilderStone.id === stone?.id
        "
        :is-added-route="isAddedRoute"
        :shape="stone?.shape.systemCode"
        @close="close"
        @select="selectStone"
    >
        <template #title>
            {{ title }}
        </template>

        <template #default>
            <StoneDetails
                v-if="stone"
                :data-qa-stone-modal-props="
                    JSON.stringify({
                        shape: stone.shape.systemCode,
                    })
                "
                :image
            >
                <template #default>
                    <dl class="details">
                        <div class="details-item">
                            <dt
                                class="title"
                                role="heading"
                                aria-level="4"
                            >
                                {{ t('diamond_search.price') }}
                                <LazyTooltip
                                    inline
                                    path="/diamond-search/gemstones/price"
                                    :page-type="pageType"
                                />
                            </dt>

                            <dd class="description">
                                {{ price }}
                            </dd>
                        </div>

                        <div class="details-item">
                            <dt
                                class="title"
                                role="heading"
                                aria-level="4"
                            >
                                {{ t('diamond_search.property_cut') }}
                                <LazyTooltip
                                    inline
                                    path="/diamond-search/gemstones/cut"
                                    :page-type="pageType"
                                />
                            </dt>

                            <dd class="description">
                                {{
                                    stone.cut.translation.name ||
                                    stone.cut.systemCode
                                }}
                            </dd>
                        </div>

                        <div class="details-item">
                            <dt
                                class="title"
                                role="heading"
                                aria-level="4"
                            >
                                {{ t('diamond_search.property_carat_weight') }}
                                <LazyTooltip
                                    inline
                                    path="/diamond-search/gemstones/carat-weight"
                                    :page-type="pageType"
                                />
                            </dt>

                            <dd class="description">
                                {{ Number(stone.caratWeight).toFixed(2) }}ct
                            </dd>
                        </div>

                        <div class="details-item">
                            <dt
                                class="title"
                                role="heading"
                                aria-level="4"
                            >
                                {{ t('diamond_search.gemstone_type') }}
                            </dt>

                            <dd class="description">
                                {{
                                    stone.color.translation.name ||
                                    stone.color.systemCode
                                }}
                                {{
                                    stone.crystal.translation.name ||
                                    stone.crystal.systemCode
                                }}
                            </dd>
                        </div>

                        <div class="details-item">
                            <dt
                                class="title"
                                role="heading"
                                aria-level="4"
                            >
                                {{ t('diamond_search.gemstone_colour_grade') }}
                                <LazyTooltip
                                    inline
                                    path="/diamond-search/gemstones/colour-grade"
                                    :page-type="pageType"
                                />
                            </dt>

                            <dd class="description">
                                {{
                                    stone.colorGrade.translation.name ||
                                    stone.colorGrade.systemCode
                                }}
                            </dd>
                        </div>

                        <div class="details-item">
                            <dt
                                class="title"
                                role="heading"
                                aria-level="4"
                            >
                                {{ t('diamond_search.property_clarity') }}
                                <LazyTooltip
                                    inline
                                    path="/diamond-search/gemstones/clarity"
                                    :page-type="pageType"
                                />
                            </dt>

                            <dd class="description">
                                {{
                                    stone.clarity.translation.name ||
                                    stone.clarity.systemCode
                                }}
                            </dd>
                        </div>
                    </dl>

                    <ShopWithConfidence
                        :ninety-day-returns
                        ethically-sourced-gemstones
                        quality-guarantee
                        :page-type="pageType"
                    />
                </template>
            </StoneDetails>
        </template>
    </NonSkuStoneModal>
</template>

<script lang="ts" setup>
import { computed, toRef } from 'vue'
import { useI18n } from 'vue-i18n'

import type { NonSkuGemstoneFragment } from '@/graphql/api'
import { useStoneImage } from '@/domain/stone/useStoneImage'
import { useStonePrice } from '../../useStonePrice'

import StoneDetails from '../../sku/StoneDetails.vue'
import NonSkuStoneModal from '@/components/stone/search/nonsku/modal/NonSkuStoneModal.vue'
import ShopWithConfidence from '../../ShopWithConfidence.vue'
import LazyTooltip from '@/components/layout/tooltip/LazyTooltip.vue'
import { useRoutes } from '@/router/routes.ts'
import { useRingBuilder } from '@/plugins/ring-builder.ts'
import type { PageType } from '@/components/cms/component/content/ContentButton.vue'

const props = defineProps<{
    stone?: NonSkuGemstoneFragment
    dataQa?: string
    pageType: PageType
}>()

const emit = defineEmits<{
    (e: 'close'): void
    (e: 'select', v: NonSkuGemstoneFragment): void
}>()

const close = () => {
    emit('close')
}

const { t } = useI18n()

const title = computed(() => {
    const st = props.stone

    if (!st) {
        return ''
    }

    return `${
        st.shape.translation.name || st.shape.systemCode
    } ${Number(st.caratWeight).toFixed(2)}ct`
})

const stoneImage = useStoneImage(() => props.stone)
const image = computed(
    () =>
        stoneImage.value && {
            ...stoneImage.value,
            alt: `${props.stone?.shape.translation.name || props.stone?.shape.systemCode} ${
                props.stone?.crystal.translation.name ||
                props.stone?.crystal.systemCode
            }`,
        }
)

const { price, totalSaleAmount } = useStonePrice(toRef(() => props.stone))

const ninetyDayReturns = computed(
    () => parseFloat(totalSaleAmount.value.amount) < 5000
)

const routes = useRoutes()

const {
    addStone,
    stone: ringBuilderStone,
    ring: ringBuilderRing,
} = useRingBuilder()

const isAddedRoute = computed(
    () =>
        ringBuilderRing.value &&
        routes.ringBuilder.ring(ringBuilderRing.value, ringBuilderStone.value)
)

const selectStone = async () => {
    const st = props.stone

    if (st) {
        await addStone(st)

        emit('select', st)
    }
}
</script>
